import {
  type ComponentProps,
  type PointerEvent,
  type ReactNode,
  useId,
  useRef,
  useState,
} from 'react'
import { ToggleGroup as ToggleGroupPrimitive } from '@base-ui/react/toggle-group'

import { cn } from '@gto/ui/cn'
import { HelpIconTooltip } from '@gto/ui/components/help-icon-tooltip'
import type { BrushOption } from '@gto/ui/hooks/use-brush-selection'
import { useBrushSelection } from '@gto/ui/hooks/use-brush-selection'
import { typography } from '@gto/ui/typography'

import { type ToggleButtonSize, toggleStyles } from './toggle-button.styles'
import { ToggleButtonGroupContext } from './toggle-button-group-context'
import { escapeToggleValue, unescapeToggleValue } from './toggle-button-value'

type ToggleGroupBaseProps = Omit<
  ComponentProps<typeof ToggleGroupPrimitive>,
  'value' | 'defaultValue' | 'onValueChange' | 'multiple'
> & {
  size?: ToggleButtonSize
  disabled?: boolean
  /**
   * Size each entry to its own label rather than to an equal share of the row, so
   * `Combined` takes the room its word needs and `HJ` takes the room two letters need.
   *
   * Entries also stop shrinking. A row too long for its container then scrolls — the
   * caller owns the scrollport — instead of compressing every entry until its
   * `whitespace-nowrap` label paints over its neighbour.
   */
  hug?: boolean
  /**
   * Heading above the row (Figma `Label`). It names the group for assistive tech. A
   * `size="large"` group uses `input/label/large` and a 20px help icon; smaller sizes
   * use `input/label` and a 16px icon.
   */
  label?: ReactNode
  /** Help copy for the label — renders the DS help icon beside it. Needs `label`. */
  tooltip?: ReactNode
  /** Accessible name for the help trigger. Pass a localized string. */
  tooltipAriaLabel?: string
  children: ReactNode
}

type ToggleGroupSingleProps<T extends string = string> = ToggleGroupBaseProps & {
  single?: true
  value?: T
  defaultValue?: T
  onValueChange?: (value: T) => void
  // Declared so the component can take the brush props off `rest` on either arm of the
  // union. Left in `rest`, they reached the DOM as unknown attributes.
  brushable?: never
  brushOptions?: never
  brushCursor?: never
}

type ToggleGroupMultipleProps<T extends string = string> = ToggleGroupBaseProps & {
  single: false
  value?: T[]
  defaultValue?: T[]
  onValueChange?: (value: T[]) => void
  /**
   * Press and drag across the row to select a contiguous run, for a row long enough
   * that clicking each button is the wrong amount of work — thirteen card ranks, say.
   *
   * Multi-select only, because a run of one is just a click. Requires
   * {@link ToggleGroupMultipleProps.brushOptions}; without it there is no order to run
   * along and the group stays click-only.
   */
  brushable?: boolean
  /**
   * The brushable values IN LAYOUT ORDER, which is the order a run walks. A neutral
   * `Any` belongs outside this list: a drag from the first button must not sweep up the
   * thing that means "no selection". Disabled entries are swept THROUGH, never into.
   */
  brushOptions?: readonly BrushOption<T>[]
  /**
   * Shows the brush cursor over a brushing row — its buttons and the gaps between them.
   * Off by default, so a brushing row keeps the buttons' own cursor unless it asks.
   */
  brushCursor?: boolean
}

export type ToggleButtonGroupProps<T extends string = string> =
  | ToggleGroupSingleProps<T>
  | ToggleGroupMultipleProps<T>

const normalizeToArray = <T extends string>(value: T | T[] | undefined): T[] | undefined => {
  if (value === undefined) {
    return undefined
  }

  return Array.isArray(value) ? value : [value]
}

export const ToggleButtonGroup = <T extends string = string>(props: ToggleButtonGroupProps<T>) => {
  const {
    size = 'medium',
    orientation = 'horizontal',
    single = true,
    disabled = false,
    hug = false,
    className,
    children,
    value: controlledValue,
    defaultValue,
    onValueChange: _onValueChange,
    ref: forwardedRef,
    brushable: _brushable,
    brushOptions: _brushOptions,
    brushCursor: _brushCursor,
    label,
    tooltip,
    tooltipAriaLabel,
    ...rest
  } = props
  // Destructured off the union rather than the base, so a single-select group cannot
  // ask for a gesture that only means something across several values.
  const brushOptions = props.single === false ? props.brushOptions : undefined
  const brushing = props.single === false && props.brushable === true && brushOptions !== undefined
  const { group } = toggleStyles({ orientation })
  const layoutId = useId()
  const labelId = useId()
  const hasLabel = label !== undefined
  const multiple = !single

  const normalizedDefaultValue = normalizeToArray(defaultValue) ?? []
  const [internalValue, setInternalValue] = useState<string[]>(normalizedDefaultValue)

  const normalizedControlledValue = normalizeToArray(controlledValue)
  const currentValue = normalizedControlledValue ?? internalValue

  const handleValueChange = (newValue: string[]) => {
    if (single && newValue.length === 0) {
      return
    }

    if (normalizedControlledValue === undefined) {
      setInternalValue(newValue)
    }

    if (props.single === false) {
      props.onValueChange?.(newValue as T[])
    } else {
      props.onValueChange?.(newValue[0] as T)
    }
  }

  /**
   * What the brush sees: the selected values that are actually brushable, in layout
   * order. Narrowing here rather than casting `currentValue` is also the correct
   * reading — a neutral `Any` is not a value the gesture can paint, so a run must not
   * carry it back out.
   */
  const brushedValue =
    brushOptions
      ?.filter((option) => currentValue.includes(option.value))
      .map((option) => option.value) ?? []

  /** Whether the last press began a brush gesture — see `onClickCapture`. */
  const startedGestureRef = useRef(false)

  const brush = useBrushSelection<T>({
    brushCursor: props.single === false && props.brushCursor === true,
    disabled,
    onChange: handleValueChange,
    options: brushOptions ?? [],
    value: brushedValue,
  })

  /**
   * Starts the gesture from whichever button the press landed on, resolved by walking
   * up from the event target — the press arrives on the label or the indicator inside
   * the button just as often as on the button itself.
   *
   * Whether a gesture actually began is remembered, because the click that follows is
   * only the gesture's to swallow if there was one. A press on a button OUTSIDE
   * `brushOptions` — a neutral `Any` — starts nothing and must reach the toggle group
   * as an ordinary click.
   */
  const onPointerDownCapture = (event: PointerEvent<Element>) => {
    startedGestureRef.current = false

    if (!brushing || !(event.target instanceof Element)) {
      return
    }

    const node = event.target.closest<HTMLElement>('[data-brush-value]')
    const pressed = brushOptions.find((option) => option.value === node?.dataset.brushValue)

    if (pressed !== undefined) {
      startedGestureRef.current = brush.startBrush(event, pressed.value)
    }
  }

  /**
   * Keeps the underlying toggle group from selecting on a POINTER click the gesture has
   * already answered — the double-toggle `useBrushSelection` documents under "Why the
   * gesture is built on pointer events and not click". The CAPTURE phase is what gets
   * ahead of the button's own handler.
   *
   * Two clicks must still pass. A keyboard activation carries `detail === 0` and has no
   * gesture behind it, so the toggle group stays the thing that handles Space and Enter.
   * And a click on a button the gesture declined — the neutral `Any` — was never
   * answered by anything, so swallowing it would leave that button dead.
   */
  const onClickCapture = (event: {
    detail: number
    target: EventTarget | null
    stopPropagation: () => void
  }) => {
    if (startedGestureRef.current && event.detail !== 0) {
      event.stopPropagation()

      startedGestureRef.current = false

      return
    }

    startedGestureRef.current = false

    // A keyboard activation changes the selection without the gesture seeing it, so the
    // gesture is told where the user just was. Otherwise a following Shift-click extends
    // from whatever the last DRAG anchored on — Tab to `T`, press Space, shift-click `5`,
    // and the run comes back as A..5 rather than T..5.
    if (event.detail === 0 && brushing && event.target instanceof Element) {
      const node = event.target.closest<HTMLElement>('[data-brush-value]')
      const activated = brushOptions.find((option) => option.value === node?.dataset.brushValue)

      if (activated === undefined) {
        brush.clearAnchor()
      } else {
        brush.anchorAt(activated.value)
      }
    }
  }

  /**
   * Gives the gesture the container it has to measure WITHOUT taking the consumer's ref
   * away from it. Only installed while brushing, so a group that does not brush keeps
   * passing its ref straight through and behaves exactly as it did before the gesture
   * existed — this used to be `ref={brushing ? … : undefined}` after the prop spread,
   * which silently overwrote a caller's ref with nothing.
   */
  const setContainer = (node: HTMLDivElement | null) => {
    brush.attachContainer(node)

    if (typeof forwardedRef === 'function') {
      forwardedRef(node)
    } else if (forwardedRef !== null && forwardedRef !== undefined) {
      forwardedRef.current = node
    }
  }

  // With a label the wrapper takes the caller's layout class and the row fills it.
  const groupClassName = cn(
    group(),
    // `touch-pan-y`: a vertical swipe scrolls the page and a horizontal one brushes.
    brushing && 'touch-pan-y select-none',
    hasLabel ? 'w-full' : className,
  )

  const groupNode = (
    <ToggleButtonGroupContext
      value={{
        brushValues: brushing ? brushOptions.map((option) => option.value) : [],
        disabled,
        hug,
        layoutId,
        selectedValues: currentValue,
        single,
        size,
      }}
    >
      <ToggleGroupPrimitive
        {...rest}
        ref={brushing ? setContainer : forwardedRef}
        aria-labelledby={hasLabel ? labelId : rest['aria-labelledby']}
        className={groupClassName}
        disabled={disabled}
        multiple={multiple}
        orientation={orientation}
        // Escaped across the Base UI boundary and unescaped straight back, so a falsy value
        // (the empty string is a real key in plenty of domains) survives the round trip. The
        // BRUSH deliberately calls `handleValueChange` directly with raw values, which is why
        // the unescaping lives here on the primitive's own handler and not inside it.
        value={currentValue.map(escapeToggleValue)}
        onValueChange={(next: string[]) => handleValueChange(next.map(unescapeToggleValue))}
        {...(brushing ? { ...brush.containerProps, onClickCapture, onPointerDownCapture } : {})}
      >
        {children}
      </ToggleGroupPrimitive>
    </ToggleButtonGroupContext>
  )

  if (!hasLabel) {
    return groupNode
  }

  const isLarge = size === 'large'
  const labelTypography = isLarge ? typography['input/label/large'] : typography['input/label']
  const labelClassName = cn(
    labelTypography,
    disabled ? 'text-text-disabled' : 'text-text-secondary',
  )

  return (
    <div className={cn('flex flex-col items-start gap-1', className)}>
      <div className={cn('flex w-full items-center gap-1', isLarge ? 'px-3' : 'px-2')}>
        <span
          className={labelClassName}
          id={labelId}
        >
          {label}
        </span>
        {tooltip !== undefined && (
          <HelpIconTooltip
            ariaLabel={tooltipAriaLabel}
            content={tooltip}
            iconClassName="text-text-secondary"
            size={isLarge ? 'md' : 'sm'}
          />
        )}
      </div>
      {groupNode}
    </div>
  )
}
